import React, { useState, useEffect } from "react";
import { BarChart as MuiBarChart } from "@mui/x-charts/BarChart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  LabelList,
  Label,
  Legend,
} from "recharts";
import {
  FaMoneyBillWave,
  FaWallet,
  FaUniversity,
  FaArrowDown,
  FaArrowUp,
  FaBell,
} from "react-icons/fa";
import CylinderInventoryProgress from "./CylinderInventoryProgress";
import { FilterPopup } from "./FilterPopup";
import { Tooltip as AntTooltip } from "antd";
import "./ProductDashboard.css";

/* -------------------- Refined Palettes -------------------- */
const PALETTE_TEAL = ["#005F73", "#0A9396", "#94D2BD", "#E9D8A6"];
const PALETTE_PURPLE = ["#4F46E5", "#7C3AED", "#C026D3", "#818CF8"];
const PALETTE_WARM = ["#F59E0B", "#EF4444", "#FB7185", "#FBBF24"];
const PALETTE_GREEN = ["#166534", "#10B981", "#84CC16", "#A3E635"];
const PALETTE_MIXED = ["#0EA5E9", "#6366F1", "#F43F5E", "#8B5CF6"];

const getRunningFYLabel = () => {
  const today = new Date();
  const year = today.getFullYear();
  const fyEnd = today.getMonth() >= 3 ? year + 1 : year;
  return `${fyEnd - 1}-${String(fyEnd).slice(2)}`;
};

const hasChartData = (data = []) =>
  Array.isArray(data) && data.some((d) => Number(d?.value) > 0);

const buildFYList = (registeredFY) => {
  const today = new Date();
  const currentFYEnd =
    today.getMonth() >= 3 ? today.getFullYear() + 1 : today.getFullYear();
  const registrationFYEnd = 2000 + Number(registeredFY) + 1;
  const list = [];
  for (let y = currentFYEnd; y >= registrationFYEnd; y--) {
    list.push(`${y - 1}-${String(y).slice(2)}`);
  }
  return list;
};

const NoData = ({ text = "No data available" }) => (
  <div className="no-data">{text}</div>
);

const FILTER_KEYS = {
  PURCHASES: "filter_purchases",
  SALES: "filter_sales",
  CASH_CREDIT: "filter_cash_credit",
  PSE: "filter_pse",
  NC_DBC: "filter_nc_dbc",
};

/* -------------------- Responsive Hook -------------------- */
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, [query]);

  return matches;
};

/* -------------------- Main Component -------------------- */
export default function ProductDashboard({
  dashboard_data = {},
  purchaseSalesData,
  salesData,
  cashCreditData,
  purchaseSalesExpensesData,
  ncDbcData,
  onPurchaseSalesFilter = () => { },
  onSalesFilter = () => { },
  onCashCreditFilter = () => { },
  onPurchaseSalesExpensesFilter = () => { },
  onNcDbcFilter = () => { },
}) {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  // console.log(purchaseSalesData, "purchasssssss");
  const [isPurchaseFiltered, setIsPurchaseFiltered] = useState(false);
  const [isCashCreditFiltered, setIsCashCreditFiltered] = useState(false);
  const [isNcDbcFiltered, setIsNcDbcFiltered] = useState(false);
  const [isSalesFiltered, setIsSalesFiltered] = useState(false);
  const [dashboardSnapshot, setDashboardSnapshot] = useState(null);

  const deviceClass = isMobile ? "mobile" : isTablet ? "tablet" : "desktop";

  useEffect(() => {
    if (!dashboardSnapshot && dashboard_data) {
      setDashboardSnapshot(dashboard_data);
    }
  }, [dashboard_data]);

  const formatNumber = (value) => {
    if (!value || value === "0" || value === 0) return "0";
    return parseFloat(value).toLocaleString("en-IN");
  };

  const getBankBalance = (bankCode) => {
    if (!dashboard_data?.bank || !Array.isArray(dashboard_data.bank))
      return "0.00";
    const account = dashboard_data.bank.find((v) => v.BANK_CODE === bankCode);
    return account?.ACC_CB || "0.00";
  };

  const getLoadACBalance = () => {
    const tarBalance = parseFloat(getBankBalance("TAR ACCOUNT")) || 0;
    const starBalance = parseFloat(getBankBalance("STAR ACCOUNT")) || 0;
    return (tarBalance + starBalance).toFixed(2);
  };

  const normalizePurchaseData = (source = []) =>
    Array.isArray(source)
      ? source
        .filter((i) => ["DOMESTIC", "COMMERCIAL", "ARB"].includes(i.type))
        .map((i) => ({
          name:
            i.type === "DOMESTIC"
              ? "DOM"
              : i.type === "COMMERCIAL"
                ? "COM"
                : "ARB",
          value: Number(i.total || 0),
        }))
      : [];

  const normalizeCashCreditData = (source) =>
    source?.CASH_SALES_REVENUE !== undefined
      ? [
        { name: "Cash", value: Number(source.CASH_SALES_REVENUE || 0) },
        { name: "Credit", value: Number(source.CREDIT_SALES_REVENUE || 0) },
      ]
      : [];

  const normalizeCategorySalesData = (source) => {
    if (!source) return [];
    return [
      {
        name: "DOM",
        value: Number(source.DOM_SALES || 0),
        qty: Number(source.DOM_SALE_QTY || 0),
      },
      {
        name: "COM",
        value: Number(source.COM_SALES || 0),
        qty: Number(source.COM_SALE_QTY || 0),
      },
      {
        name: "ARB",
        value: Number(source.ARB_SALES || 0),
        qty: Number(source.ARB_SALE_QTY || 0),
      },
    ];
  };
  const dummySource = {
    rc_tv: [
      { category: "rc", dom: "10", com: "5", total: "10" },
      { category: "tv", dom: "2", com: "5", total: "10" },
    ],
    nc_dbc: [
      { category: "nc", dom: "10", com: "5", arb: "10", total: "10" },
      { category: "dbc", dom: "2", com: "5", arb: "10", total: "10" },
    ],
  };
  const normalizeNcDbcData = (source) => {
    if (!source) return null;

    // Merge BOTH arrays so all 4 categories (NC, DBC, RC, TV) appear together
    const ncDbcArr = Array.isArray(source.nc_dbc) ? source.nc_dbc : [];
    const rcTvArr = Array.isArray(source.rc_tv) ? source.rc_tv : [];
    const rawArr = [...ncDbcArr, ...rcTvArr];
    if (!rawArr.length) return null;

    const xLabels = rawArr.map((item) => (item.category ?? "").toUpperCase());
    const commData = rawArr.map((item) => Number(item.com ?? item.comm ?? 0));
    const domData = rawArr.map((item) => Number(item.dom ?? 0));

    const hasArb = rawArr.some((item) => item.arb !== undefined && item.arb !== null);
    const arbData = hasArb
      ? rawArr.map((item) =>
        Object.prototype.hasOwnProperty.call(item, "arb")
          ? Number(item.arb ?? 0)
          : null
      )
      : null;

    return { xLabels, commData, domData, arbData, hasArb };
  };

  // const cylinderInventory =
  //   dashboard_data?.godown_stock
  //     ?.filter((item) => item.CATEGORY === "Cylinder")
  //     ?.map((item) => ({
  //       label: item.GODOWN_NAME,
  //       weight: item.GODOWN_NAME,
  //       fulls: parseInt(item.FULLS ?? item.CLOSING_STOCK) || 0,
  //       empties: parseInt(item.EMPTIES ?? item.CLOSING_STOCK_EMPTIES) || 0,
  //       fillColor: "#005F73",
  //     })) || [];

  const cylinderInventory = (() => {
    const filtered = (dashboard_data?.godown_stock ?? []).filter(
      (item) => item.CAT_NAME === "DOMESTIC" || item.CAT_NAME === "COMMERCIAL",
    );

    // Group by normalized weight key and SUM fulls + empties
    // so that "19 Kg LOT" (0 stock) and "19 Kg Refill" (1429 stock)
    // both contribute to the "19KG" slot instead of only the first match showing.
    const grouped = {};
    filtered.forEach((item) => {
      const name = item.PRODUCT_NAME ?? "";
      let key;
      if (name.includes("5") && !name.includes("14") && !name.includes("19") && !name.includes("47")) key = "5KG";
      else if (name.includes("14")) key = "14.2KG";
      else if (name.includes("19")) key = "19KG";
      else if (name.includes("47")) key = "47KG";
      else key = name;

      const fulls = parseInt(item.CLOSING_STOCK) || 0;
      const empties = parseInt(item.CLOSING_STOCK_EMPTIES) || 0;

      if (!grouped[key]) {
        grouped[key] = {
          label: `Total-${item.PROD_CODE}`,
          weight: name,
          fulls: 0,
          empties: 0,
          fillColor: item.CAT_NAME === "DOMESTIC" ? "#005F73" : "#84CC16",
        };
      }
      grouped[key].fulls += fulls;
      grouped[key].empties += empties;
    });

    return Object.values(grouped);
  })();

 const arbInventoryData = (dashboard_data?.godown_stock ?? [])
  .filter(item => item?.GODOWN_NAME === "ARB Products")
  .map(item => ({
    name: item.CATEGORY || item.GODOWN_NAME || "Unknown",
    value: Number(item.CLOSING_STOCK ?? 0),
  }));

  const buildDashboardPayload = ({
    filterBy,
    fy,
    date,
    month,
    week,
    quarter,
  }) => {
    const payload = { fy };

    switch (filterBy) {
      case "Day":
        payload.date = date;
        break;

      case "Week":
        payload.month = month;
        payload.week = week;
        break;

      case "Month":
        payload.month = month;
        break;

      case "Qtr":
        payload.quarter = quarter;
        break;

      case "Year":
        // fy already present
        break;

      default:
        break;
    }

    return payload;
  };

  const handlePurchasesFilter = (filters) => {
    setIsPurchaseFiltered(true);
    onPurchaseSalesFilter(buildDashboardPayload(filters));
  };

  const handleSalesFilter = (filters) => {
    setIsSalesFiltered(true);
    onSalesFilter(buildDashboardPayload(filters));
  };

  const handleCashCreditFilter = (filters) => {
    setIsCashCreditFiltered(true);
    onCashCreditFilter(buildDashboardPayload(filters));
  };

  const handlePurchasesSalesExpensesFilter = (filters) => {
    onPurchaseSalesExpensesFilter(buildDashboardPayload(filters));
  };

  const handleNcDbcFilter = (filters) => {
    setIsNcDbcFiltered(true);
    onNcDbcFilter(buildDashboardPayload(filters));
  };

  const purchaseChartData = isPurchaseFiltered
    ? normalizePurchaseData(purchaseSalesData?.purchase_sales)
    : normalizePurchaseData(dashboardSnapshot?.purchase_sales);

  const salesChartData = isSalesFiltered
    ? normalizeCategorySalesData(salesData)
    : normalizeCategorySalesData(dashboardSnapshot);

  const cashCreditChartData = isCashCreditFiltered
    ? normalizeCashCreditData(cashCreditData)
    : [
      {
        name: "Cash",
        value: Number(dashboardSnapshot?.CASH_SALES_REVENUE || 0),
      },
      {
        name: "Credit",
        value: Number(dashboardSnapshot?.CREDIT_SALES_REVENUE || 0),
      },
    ];

  const pseChartData = purchaseSalesExpensesData?.PURCHASE_COST
    ? [
      {
        name: "Purchases",
        value: Number(purchaseSalesExpensesData.PURCHASE_COST),
      },
      {
        name: "Sales",
        value: Number(purchaseSalesExpensesData.SALES_REVENUE),
      },
      {
        name: "Expenses",
        value: Number(purchaseSalesExpensesData.PURCHASE_COST) * 0.1,
      },
    ]
    : [
      {
        name: "Purchases",
        value: Number(dashboardSnapshot?.PURCHASE_COST || 0),
      },
      { name: "Sales", value: Number(dashboardSnapshot?.SALES_REVENUE || 0) },
      {
        name: "Expenses",
        value: Number(dashboardSnapshot?.PURCHASE_COST || 0) * 0.1,
      },
    ];

  const ncDbcChartData = normalizeNcDbcData(
    isNcDbcFiltered
      ? { nc_dbc: ncDbcData?.nc_dbc, rc_tv: ncDbcData?.rc_tv }
      : {
        nc_dbc: (dashboardSnapshot ?? dashboard_data)?.nc_dbc,
        rc_tv: (dashboardSnapshot ?? dashboard_data)?.rc_tv,
      }
  );
  // const ncDbcChartData = normalizeNcDbcData(dummySource);
  const notifications =
    dashboard_data?.alerts?.length > 0
      ? dashboard_data.alerts
      : [
        {
          id: 0,
          text: "No notifications available",
        },
      ];
  // [
  //   {
  //     id: 1,
  //     text: "Invoice pending approval",
  //     type: "warning",
  //     time: "2 min ago",
  //   },
  //   {
  //     id: 2,
  //     text: "Payment received",
  //     type: "success",
  //     time: "10 min ago",
  //   },
  //   { id: 3, text: "Low stock alert", type: "danger", time: "1 hr ago" },
  // ];

  return (
    <div className={`product-dashboard-root ${deviceClass}`}>
      <div className={`dashboard-grid ${deviceClass}`}>
        <CylinderInventoryCard
          cylinderInventory={cylinderInventory}
          deviceClass={deviceClass}
        />

        <div className="combined-chart-grid desktop-four">
          <div className={`inner-chart-cell ${deviceClass}`}>
            <ChartCardBare
              title="Purchases"
              showPeriod
              persistKey={FILTER_KEYS.PURCHASES}
              onFilterApply={handlePurchasesFilter}
              deviceClass={deviceClass}
            >
              <div className={`chart-container ${deviceClass}`}>
                {hasChartData(purchaseChartData) ? (
                  <PieBlock palette={PALETTE_PURPLE} data={purchaseChartData} />
                ) : (
                  <NoData text="No data available" />
                )}
              </div>
            </ChartCardBare>
          </div>

          <div
            className={`inner-chart-cell ${deviceClass} ${!isMobile && !isTablet ? "border-left" : ""}`}
          >
            <ChartCardBare
              title="Sales"
              showPeriod
              persistKey={FILTER_KEYS.SALES}
              onFilterApply={handleSalesFilter}
              deviceClass={deviceClass}
            >
              <div className={`chart-container ${deviceClass}`}>
                {hasChartData(salesChartData) ? (
                  <PieBlock
                    palette={PALETTE_WARM}
                    data={salesChartData}
                    isDonut
                  />
                ) : (
                  <NoData text="No data available" />
                )}
              </div>
            </ChartCardBare>
          </div>

          <div
            className={`inner-chart-cell ${deviceClass} ${!isMobile && !isTablet ? "border-top" : ""}`}
          >
            <ChartCardBare
              title="Cash / Credit Sales"
              showPeriod
              persistKey={FILTER_KEYS.CASH_CREDIT}
              onFilterApply={handleCashCreditFilter}
              deviceClass={deviceClass}
            >
              <div className={`chart-container ${deviceClass}`}>
                {hasChartData(cashCreditChartData) ? (
                  <PieBlock
                    palette={PALETTE_MIXED}
                    data={cashCreditChartData}
                    isDonut
                  />
                ) : (
                  <NoData text="No data available" />
                )}
              </div>
            </ChartCardBare>
          </div>

          <div
            className={`inner-chart-cell ${deviceClass} ${!isMobile && !isTablet ? "border-top border-left" : ""}`}
          >
            <ChartCardBare
              title="Purchases, Sales & Expenses"
              showPeriod
              persistKey={FILTER_KEYS.PSE}
              onFilterApply={handlePurchasesSalesExpensesFilter}
              deviceClass={deviceClass}
            >
              <div className={`chart-container ${deviceClass}`}>
                {hasChartData(pseChartData) ? (
                  <PieBlock palette={PALETTE_GREEN} data={pseChartData} />
                ) : (
                  <NoData text="No data available" />
                )}
              </div>
            </ChartCardBare>
          </div>
        </div>
        <div className={`tablet-left-column ${deviceClass}`}>
          <div className="kpi-grid">
            <KPIBlock
              title="Cash Balance"
              value={`₹ ${formatNumber(dashboard_data?.cash_balance)}`}
              color="#0EA5E9"
              icon={<FaMoneyBillWave />}
              deviceClass={deviceClass}
            />
            <KPIBlock
              title="Payables"
              value={`₹ ${formatNumber(dashboard_data?.payables)}`}
              color="#EF4444"
              icon={<FaArrowDown />}
              deviceClass={deviceClass}
            />
            <KPIBlock
              title="Receivables"
              value={`₹ ${formatNumber(dashboard_data?.receivables)}`}
              color="#10B981"
              icon={<FaArrowUp />}
              deviceClass={deviceClass}
            />
            <KPIBlock
              title="Load AC"
              value={`₹ ${formatNumber(dashboard_data?.bank?.[0]?.ACC_CB || "0")}`}
              color="#6366F1"
              icon={<FaUniversity />}
              deviceClass={deviceClass}
            />
            <KPIBlock
              title="SV/TV"
              value={`₹ ${formatNumber(dashboard_data?.bank?.[1]?.ACC_CB || "0")}`}
              color="#8B5CF6"
              icon={<FaUniversity />}
              deviceClass={deviceClass}
            />
            <KPIBlock
              title="SV/TV + Load AC"
              value={`₹ ${formatNumber(getLoadACBalance())}`}
              color="#F59E0B"
              icon={<FaWallet />}
              deviceClass={deviceClass}
            />
          </div>
        </div>
        {/* ================= ROW 2 ================= */}

        {/* ARB INVENTORY */}
        <div className={`dashboard-card arb-desktop ${deviceClass}`}>
          <div className={`card-header ${deviceClass}`}>ARB Inventory</div>
          <div className={`chart-body ${deviceClass}`}>
            <BarBlock palette={PALETTE_TEAL} data={arbInventoryData} />
          </div>
        </div>

        {/* NC / DBC SALES */}
        <div className={`dashboard-card nc-dbc-desktop ${deviceClass}`}>
          <ChartCardBare
            title="NC / DBC Sales"
            showPeriod
            persistKey={FILTER_KEYS.NC_DBC}
            onFilterApply={handleNcDbcFilter}
            deviceClass={deviceClass}
          >
            <div className={`chart-container ${deviceClass}`}>
              {ncDbcChartData ? (
                <NcDbcBarBlock data={ncDbcChartData} />
              ) : (
                <NoData />
              )}
            </div>
          </ChartCardBare>
        </div>
        <div className={`notification-row ${deviceClass}`}>
          <div className={`notification-card ${deviceClass}`}>
            <div className={`notification-header ${deviceClass}`}>
              <span>🔔 Notifications</span>
              <span className={`notification-badge ${deviceClass}`}>
                {notifications.length}
              </span>
            </div>

            <div className="notification-list">
              {notifications.map((n, i) => (
                <div key={i} className={`notification-item ${deviceClass}`}>
                  <div
                    className={`notification-dot ${n.type === "danger" ? "danger" : "warning"}`}
                  />
                  <div>
                    <div className={`notification-text ${deviceClass}`}>
                      {n.text || n.message}
                    </div>
                    <div className={`notification-time ${deviceClass}`}>
                      {n.time}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ----------------------Tablet View Nc/db--------------------------- */}
        {/* {isTablet && (
          <div className={`dashboard-card   tablet-nc-dbc-card ${deviceClass}`} style={{ minHeight: "unset" }}>
            <ChartCardBare
              title="NC / DBC Sales"
              showPeriod
              persistKey={FILTER_KEYS.NC_DBC}
              onFilterApply={handleNcDbcFilter}
              deviceClass={deviceClass}
            >
              <div className={`chart-container ${deviceClass}`}>
                {hasChartData(ncDbcChartData) ? (
                  <BarBlock palette={PALETTE_PURPLE} data={ncDbcChartData} />
                ) : (
                  <NoData />
                )}
              </div>
            </ChartCardBare>
          </div>
        )} */}
        {/* ---------------------- */}

        {/* <div className={`bottom-row ${deviceClass}`}>
          {!isTablet && (
            <div className={`dashboard-card ${deviceClass}`} style={{ minHeight: "unset" }}>
              <ChartCardBare
                title="NC / DBC Sales"
                showPeriod
                persistKey={FILTER_KEYS.NC_DBC}
                onFilterApply={handleNcDbcFilter}
                deviceClass={deviceClass}
              >
                <div className={`chart-container ${deviceClass}`}>
                  {hasChartData(ncDbcChartData) ? (
                    <BarBlock palette={PALETTE_PURPLE} data={ncDbcChartData} />
                  ) : (
                    <NoData />
                  )}
                </div>
              </ChartCardBare>
            </div>
          )}

          <div className={`dashboard-card combined-desktop ${deviceClass}`} style={{ padding: 0, overflow: "hidden" }}>
          </div>
              
        </div> */}
      </div>
    </div>
  );
}

/* -------------------- Sub-Components -------------------- */

const BarBlock = ({ palette, data }) => {
  const isMany = data.length > 4;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{ top: 10, right: 10, left: -20, bottom: isMany ? 45 : 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
        <XAxis
          dataKey="name"
          axisLine={false}
          tickLine={false}
          interval={0}
          tick={
            isMany
              ? { fontSize: 9, fontWeight: 700, fill: "#475569", angle: -35, textAnchor: "end", dy: 4 }
              : { fontSize: 11, fontWeight: 700, fill: "#475569" }
          }
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 10, fill: "#64748b" }}
        />
        <Tooltip
          cursor={{ fill: "#f8fafc" }}
          contentStyle={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            fontSize: "11px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          }}
        />
        <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={isMany ? 28 : 50}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={palette[index % palette.length]} />
          ))}
          <LabelList
            dataKey="value"
            position="top"
            style={{ fontSize: isMany ? "10px" : "12px", fontWeight: "900", fill: "#1e293b" }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};


/* ── NC / DBC SALES — Recharts stacked bar ── */
const NcDbcBarBlock = ({ data }) => {
  if (!data?.xLabels?.length) return <NoData />;

  const { xLabels, commData, domData, arbData, hasArb } = data;

  // Build recharts [{name, com, dom, arb, hasOwnArb}] — hasOwnArb true only for NC/DBC
  const chartData = xLabels.map((name, i) => ({
    name,
    com: Number(commData[i] ?? 0),
    dom: Number(domData[i] ?? 0),
    arb: hasArb && arbData[i] != null ? Number(arbData[i]) : 0,
    hasOwnArb: hasArb && arbData[i] != null,
  }));

  // Custom tooltip — recharts works fine with functions defined locally
  const NcDbcTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const entry = payload[0]?.payload ?? {};

    const rows = [
      { key: "com", label: "Commercial", color: "#4F46E5" },
      { key: "dom", label: "Domestic", color: "#0A9396" },
      ...(hasArb ? [{ key: "arb", label: "ARB", color: "#F59E0B" }] : []),
    ].filter((r) => {
      if (r.key === "arb") return entry.hasOwnArb && entry.arb > 0;
      return (entry[r.key] ?? 0) > 0;
    });

    const total = (entry.com ?? 0) + (entry.dom ?? 0) +
      (entry.hasOwnArb ? (entry.arb ?? 0) : 0);

    return (
      <div style={{
        background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.13)", padding: "10px 14px",
        minWidth: "155px", pointerEvents: "none",
      }}>
        <div style={{
          fontSize: "11px", fontWeight: "800", color: "#1e293b",
          textTransform: "uppercase", letterSpacing: "0.07em",
          borderBottom: "1px solid #f1f5f9", paddingBottom: "6px", marginBottom: "8px",
        }}>{entry.name}</div>

        {rows.length > 0 ? rows.map((r) => (
          <div key={r.key} style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: "18px", marginBottom: "5px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{
                width: "8px", height: "8px", borderRadius: "50%",
                background: r.color, flexShrink: 0, boxShadow: `0 0 0 2px ${r.color}33`,
              }} />
              <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
                {r.label}
              </span>
            </div>
            <span style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a" }}>
              {entry[r.key]}
            </span>
          </div>
        )) : (
          <div style={{ fontSize: "11px", color: "#94a3b8", textAlign: "center" }}>No data</div>
        )}

        {rows.length > 0 && (
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderTop: "1px solid #f1f5f9", marginTop: "7px", paddingTop: "6px",
          }}>
            <span style={{ fontSize: "11px", fontWeight: "700", color: "#475569" }}>Total</span>
            <span style={{
              fontSize: "13px", fontWeight: "800", color: "#0f172a",
              background: "#f8fafc", borderRadius: "4px", padding: "1px 6px",
            }}>{total}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
        <XAxis dataKey="name" axisLine={false} tickLine={false}
          tick={{ fontSize: 11, fontWeight: 700, fill: "#475569" }} />
        <YAxis axisLine={false} tickLine={false} width={40} allowDecimals={false}
          tick={{ fontSize: 10, fill: "#64748b" }} />
        <Tooltip content={NcDbcTooltip} cursor={{ fill: "#f8fafc" }} />
        <Legend wrapperStyle={{ fontSize: "10px", paddingTop: "4px" }} iconSize={8}
          formatter={(v) => v === "com" ? "Commercial" : v === "dom" ? "Domestic" : "ARB"} />
        <Bar dataKey="com" name="com" stackId="a" fill="#4F46E5" />
        <Bar dataKey="dom" name="dom" stackId="a" fill="#0A9396" />
        {hasArb && <Bar dataKey="arb" name="arb" stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} />}
      </BarChart>
    </ResponsiveContainer>
  );
};

const PieBlock = ({ palette, data, isDonut }) => {
  const totalValue = data.reduce((sum, entry) => sum + entry.value, 0);
  const formatLabel = (val) => {
    const num = Number(val);
    if (num >= 10000000) return (num / 10000000).toFixed(1) + "Cr";
    if (num >= 100000) return (num / 100000).toFixed(1) + "L";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toFixed(0);
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={isDonut ? "48%" : 0}
          outerRadius="70%"
          paddingAngle={2}
          stroke="#ffffff"
          strokeWidth={2}
          labelLine={false}
          isAnimationActive={false}
          minAngle={10}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={palette[index % palette.length]}
            />
          ))}
          <LabelList
            dataKey="value"
            position="inside"
            formatter={formatLabel}
            style={{
              fontSize: "10px",
              fontWeight: "bold",
              fill: "#ffffff",
              textShadow: "0 1px 2px rgba(0,0,0,0.8)",
            }}
            angle={0}
            offset={10}
          />
        </Pie>
        {isDonut && (
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: "12px", fontWeight: "900", fill: "#0f172a" }}
          >
            {formatLabel(totalValue)}
          </text>
        )}
        <Tooltip
          formatter={(value, name) => [`₹${formatLabel(value)}`, name]}
          contentStyle={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            fontSize: "11px",
            padding: "6px",
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: "9px", paddingTop: "0px" }}
          iconSize={8}
          layout="horizontal"
          align="center"
        />
      </PieChart>
    </ResponsiveContainer>
  );
};
const getCurrentFY = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const fyEnd = month >= 4 ? year + 1 : year;
  return `${fyEnd - 1}-${String(fyEnd).slice(2)}`;
};

const getCurrentQuarter = () => {
  const m = new Date().getMonth() + 1;
  if (m >= 4 && m <= 6) return "1";
  if (m >= 7 && m <= 9) return "2";
  if (m >= 10 && m <= 12) return "3";
  return "4"; // Jan–Mar
};

const getCurrentWeekOfMonth = () => {
  const today = new Date();
  const day = today.getDate();
  if (day <= 7) return "1";
  if (day <= 14) return "2";
  if (day <= 21) return "3";
  return "4";
};

const getTodayISO = () => new Date().toISOString().split("T")[0];

const getCurrentMonth = () =>
  String(new Date().getMonth() + 1).padStart(2, "0");

const ChartCardBare = ({
  title,
  children,
  showPeriod,
  onFilterApply,
  persistKey,
  deviceClass,
}) => {
  /* -------------------- Helpers -------------------- */
  const resetFilters = () => {
    setFilterBy("");
    setFinancialYear(getCurrentFY());
    setSelectedDay(getTodayISO());
    setSelectedMonth(getCurrentMonth());
    setSelectedWeek(getCurrentWeekOfMonth());
    setSelectedQuarter(getCurrentQuarter());
  };

  const getCurrentFY = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const fyEnd = m >= 4 ? y + 1 : y;
    return `${fyEnd - 1}-${String(fyEnd).slice(2)}`;
  };

  const getCurrentQuarter = () => {
    const m = new Date().getMonth() + 1;
    if (m >= 4 && m <= 6) return "1";
    if (m >= 7 && m <= 9) return "2";
    if (m >= 10 && m <= 12) return "3";
    return "4";
  };

  const getCurrentWeekOfMonth = () => {
    const d = new Date().getDate();
    if (d <= 7) return "1";
    if (d <= 14) return "2";
    if (d <= 21) return "3";
    return "4";
  };

  const getCurrentMonth = () =>
    String(new Date().getMonth() + 1).padStart(2, "0");

  const getTodayISO = () => new Date().toISOString().split("T")[0];

  /* -------------------- State -------------------- */
  const saved = persistKey
    ? JSON.parse(localStorage.getItem(persistKey) || "{}")
    : {};

  const [filterBy, setFilterBy] = useState(saved.filterBy || "");
  const [financialYear, setFinancialYear] = useState(
    saved.financialYear || getCurrentFY(),
  );
  const [selectedDay, setSelectedDay] = useState(saved.day || getTodayISO());
  const [selectedWeek, setSelectedWeek] = useState(
    saved.week || getCurrentWeekOfMonth(),
  );
  const [selectedMonth, setSelectedMonth] = useState(
    saved.month || getCurrentMonth(),
  );
  const [selectedQuarter, setSelectedQuarter] = useState(
    saved.quarter || getCurrentQuarter(),
  );
  const [open, setOpen] = useState(false);

  const userData = JSON.parse(localStorage.getItem("userId") || "{}");
  const fyOptions = userData?.FY ? buildFYList(userData.FY) : [];

  /* -------------------- Apply Filter (API CALL) -------------------- */
  const handleApply = () => {
    if (!filterBy) return;

    const nowFY = getCurrentFY();
    const nowDate = getTodayISO();
    const nowMonth = getCurrentMonth();
    const nowWeek = getCurrentWeekOfMonth();
    const nowQuarter = getCurrentQuarter();

    let payload = { filterBy };

    switch (filterBy) {
      case "Day":
        payload.fy = nowFY;
        payload.date =
          selectedDay && selectedDay.length === 10 ? selectedDay : nowDate;
        break;

      case "Week":
        payload.fy =
          financialYear && financialYear.length > 0 ? financialYear : nowFY;

        payload.month =
          selectedMonth && selectedMonth.length === 2
            ? selectedMonth
            : nowMonth;

        payload.week =
          selectedWeek && selectedWeek.length > 0 ? selectedWeek : nowWeek;
        break;

      case "Month":
        payload.fy =
          financialYear && financialYear.length > 0 ? financialYear : nowFY;

        payload.month =
          selectedMonth && selectedMonth.length === 2
            ? selectedMonth
            : nowMonth;
        break;

      case "Qtr":
        payload.fy =
          financialYear && financialYear.length > 0 ? financialYear : nowFY;

        payload.quarter =
          selectedQuarter && selectedQuarter.length > 0
            ? selectedQuarter
            : nowQuarter;
        break;

      case "Year":
        payload.fy =
          financialYear && financialYear.length > 0 ? financialYear : nowFY;
        break;

      default:
        return;
    }

    if (persistKey) {
      localStorage.setItem(persistKey, JSON.stringify(payload));
    }

    onFilterApply(payload);
    setOpen(false);
  };

  /* -------------------- UI -------------------- */
  return (
    <div className={`chart-card-wrapper ${open ? "filter-open" : ""}`}>
      {/* HEADER */}
      <div className={`card-header-row ${deviceClass}`}>
        <span className={`responsive-title ${deviceClass}`}>{title}</span>

        {showPeriod && (
          <button
            onClick={() => setOpen((o) => !o)}
            className={`filter-btn ${deviceClass}`}
          >
            Period
          </button>
        )}
      </div>

      {/* FILTER OVERLAY */}
      {open && (
        <>
          <div
            className="filter-backdrop"
            onClick={() => {
              resetFilters();
              setOpen(false);
            }}
          />

          <div
            className={`inline-filter-box ${deviceClass}`}
            onWheel={(e) => e.stopPropagation()}
          >
            {/* Period Type */}

            <div className={`filter-group ${!filterBy ? "full-width" : ""}`}>
              <label className={`filter-label ${deviceClass}`}>
                Period Type
              </label>
              <select
                value={filterBy}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilterBy(val);
                }}
                className={`enhanced-select ${deviceClass}`}
              >
                <option value="">Choose Period...</option>
                <option value="Day">Date</option>
                <option value="Week">Week</option>
                <option value="Month">Month</option>
                <option value="Qtr">Quarter</option>
                <option value="Year">Year</option>
              </select>
            </div>

            {filterBy === "Day" && (
              <div className="filter-group">
                <label className="filter-label">Select Date</label>
                <input
                  type="date"
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(e.target.value)}
                  className="enhanced-select date-input"
                />
              </div>
            )}
            {/* Financial Year */}
            {["Week", "Month", "Qtr", "Year"].includes(filterBy) && (
              <div className={`filter-group ${!filterBy ? "full-width" : ""}`}>
                <label className={`filter-label ${deviceClass}`}>
                  Financial Year
                </label>
                <select
                  value={financialYear}
                  onChange={(e) => setFinancialYear(e.target.value)}
                  className={`enhanced-select ${deviceClass}`}
                >
                  {fyOptions.map((fy) => (
                    <option key={fy} value={fy}>
                      {fy}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Month */}
            {(filterBy === "Month" || filterBy === "Week") && (
              <div className={`filter-group ${!filterBy ? "full-width" : ""}`}>
                <label className={`filter-label ${deviceClass}`}>Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className={`enhanced-select ${deviceClass}`}
                >
                  <option value="04">APRIL</option>
                  <option value="05">MAY</option>
                  <option value="06">JUNE</option>
                  <option value="07">JULY</option>
                  <option value="08">AUGUST</option>
                  <option value="09">SEPTEMBER</option>
                  <option value="10">OCTOBER</option>
                  <option value="11">NOVEMBER</option>
                  <option value="12">DECEMBER</option>
                  <option value="01">JANUARY</option>
                  <option value="02">FEBRUARY</option>
                  <option value="03">MARCH</option>
                </select>
              </div>
            )}

            {/* Week */}
            {filterBy === "Week" && (
              <div className={`filter-group ${!filterBy ? "full-width" : ""}`}>
                <label className={`filter-label ${deviceClass}`}>Week</label>
                <select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  className={`enhanced-select ${deviceClass}`}
                >
                  <option value="1">Week-1 (1–7)</option>
                  <option value="2">Week-2 (8–14)</option>
                  <option value="3">Week-3 (15–21)</option>
                  <option value="4">Week-4 (22–End)</option>
                </select>
              </div>
            )}

            {/* Quarter */}
            {filterBy === "Qtr" && (
              <div className={`filter-group ${!filterBy ? "full-width" : ""}`}>
                <label className={`filter-label ${deviceClass}`}>Quarter</label>
                <select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(e.target.value)}
                  className={`enhanced-select ${deviceClass}`}
                >
                  <option value="1">Q1 (Apr - Jun)</option>
                  <option value="2">Q2 (Jul - Sep)</option>
                  <option value="3">Q3 (Oct - Dec)</option>
                  <option value="4">Q4 (Jan - Mar)</option>
                </select>
              </div>
            )}

            {/* Actions */}
            <div className="inline-filter-actions">
              <button
                className={`cancel-btn ${deviceClass}`}
                onClick={() => {
                  resetFilters();
                  setOpen(false);
                }}
              >
                Cancel
              </button>

              <button
                className={`enhanced-apply-btn ${deviceClass}`}
                onClick={handleApply}
                disabled={!filterBy}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      {/* CHART */}
      <div className={`chart-container ${deviceClass}`}>{children}</div>
    </div>
  );
};

const REQUIRED_WEIGHTS = ["5KG", "14.2KG", "19KG", "47KG"];

const normalizeWeight = (name = "") => {
  if (name.includes("5")) return "5KG";
  if (name.includes("14")) return "14.2KG";
  if (name.includes("19")) return "19KG";
  if (name.includes("47")) return "47KG";
  return name;
};

const CylinderInventoryCard = ({ cylinderInventory = [], deviceClass }) => {
  return (
    <div className={`cylinder-card ${deviceClass}`}>
      <div className={`cylinder-card-header ${deviceClass}`}>
        Cylinder Inventory
      </div>
      <div className={`cylinder-container ${deviceClass}`}>
        <div className={`cylinder-grid ${deviceClass}`}>
          {REQUIRED_WEIGHTS.map((w, index) => {
            const found = cylinderInventory.find(
              (c) => normalizeWeight(c.weight) === w,
            );
            let extraGap = "0px";
            if (deviceClass === "desktop") {
              if (index === 1) extraGap = "17px";
              if (index === 2) extraGap = "17px";
            }

            return (
              <div
                key={index}
                className="cylinder-item"
                style={{ marginRight: extraGap }}
              >
                <CylinderInventoryProgress
                  weight={found?.weight || w}
                  fulls={found?.fulls || 0}
                  empties={found?.empties || 0}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const KPIBlock = ({ title, value, color, icon, deviceClass }) => {
  return (
    <div
      className={`kpi-card ${deviceClass}`}
      style={{ borderLeft: `4px solid ${color}` }}
    >
      <div className="kpi-header">
        <span className={`kpi-icon ${deviceClass}`}>{icon}</span>
        <span className={`kpi-title ${deviceClass}`}>{title}</span>
      </div>
      <div className={`kpi-value ${deviceClass}`} style={{ color }}>
        {value}
      </div>
    </div>
  );
};
